import React, { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { seek, togglePlayPause, play, pause } from '../../store/slices/playbackSlice';
import { startScrub, updateScrub, endScrub, setIsFullscreen } from '../../store/slices/editorUISlice';
import { selectAllAssets } from '../../store/slices/assetsSlice';
import { selectTotalDuration } from '../../store/slices/timelineSlice';
import useCheckMobileScreen from '../../../../hooks/useCheckMobileScreen';
import videoPlayerPlayIcon from '../../../../assets/images/video-player/play.svg';
import videoPlayerPauseIcon from '../../../../assets/images/video-player/pause.svg';
import rewindIcon from '../../../../assets/images/create-meeting/rewind.svg';
import fullscreenIcon from '../../../../assets/images/video-player/fullscreen.svg';
import volumeIcon from '../../../../assets/images/video-player/volume.svg';
import mutedVolumeIcon from '../../../../assets/images/video-player/mutedVolume.svg';
import RecordingControls from '../RecordingControls';

function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default function SeekingBar({
  onStartRecording,
  onPauseRecording,
  onResumeRecording,
  onStopRecording,
  onCancelRecording,
  onSwitchCamera,
  hasMultipleCameras,
  currentItem,
  currentAsset,
  fullscreenContainerRef,
}) {
  const dispatch = useDispatch();
  const seekingBarRef = useRef();
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [isTouchActive, setIsTouchActive] = useState(false);
  const isMobile = useCheckMobileScreen();

  const isScrubbing = useSelector((state) => state.editorUI.isScrubbing);
  const scrubTime = useSelector((state) => state.editorUI.scrubTime);
  const isPlaying = useSelector((state) => state.playback.isPlaying);
  const currentTime = useSelector((state) => state.playback.currentTime);
  const timelineItems = useSelector((state) => state.timeline.items);
  const allAssets = useSelector(selectAllAssets);
  const totalDuration = useSelector(selectTotalDuration);
  const isRecording = useSelector((state) => state.recording.isRecording);
  const isTimerRunning = useSelector((state) => state.recording.isTimerRunning);
  const isCameraEnabled = useSelector((state) => state.recording.isCameraEnabled);
  const isScreenEnabled = useSelector((state) => state.recording.isScreenEnabled);
  const isAudioEnabled = useSelector((state) => state.recording.isAudioEnabled);
  const recordingTime = useSelector((state) => state.recording.recordingTime);

  // Find video element and listen to volume changes (must be before early return)
  useEffect(() => {
    // Only track volume for video assets
    if (currentAsset?.type !== 'video') {
      return undefined;
    }

    // Find the video element in the DOM (rendered by VideoRenderer)
    const findVideoElement = () => {
      // First try to find within fullscreen container
      if (fullscreenContainerRef?.current) {
        const video = fullscreenContainerRef.current.querySelector('video');
        if (video) return video;
      }
      // Fallback: search in the document (for preview modal or other contexts)
      return document.querySelector('video');
    };

    const videoElement = findVideoElement();
    if (!videoElement) return undefined;

    const handleVolumeChange = () => {
      setIsVideoMuted(videoElement.muted);
    };

    // Set initial state
    setIsVideoMuted(videoElement.muted);

    videoElement.addEventListener('volumechange', handleVolumeChange);

    return () => {
      videoElement.removeEventListener('volumechange', handleVolumeChange);
    };
  }, [currentAsset, currentItem, fullscreenContainerRef]);

  // Allow rendering during recording even if totalDuration is 0 (for first section)
  const isRecordingActive = isTimerRunning || isRecording || isCameraEnabled || isScreenEnabled || isAudioEnabled;
  if (totalDuration === 0 && !isRecordingActive) return null;

  // Use recordingTime as duration during recording when totalDuration is 0
  let effectiveDuration = totalDuration;
  if (effectiveDuration === 0 && isRecordingActive) {
    effectiveDuration = Math.max(recordingTime, 1);
  } else if (effectiveDuration === 0) {
    effectiveDuration = 1;
  }

  // Get time from mouse or touch event
  const getTimeFromEvent = (e) => {
    if (!seekingBarRef.current || effectiveDuration === 0) return 0;

    const rect = seekingBarRef.current.getBoundingClientRect();
    // Support both mouse and touch events
    const clientX = e.clientX !== undefined ? e.clientX : (e.touches?.[0]?.clientX ?? e.changedTouches?.[0]?.clientX);
    const clickX = clientX - rect.left;
    const percentage = Math.max(0, Math.min(clickX / rect.width, 1));
    return percentage * effectiveDuration;
  };

  // Handle seeking bar click
  const handleSeekingBarClick = (e) => {
    if (isScrubbing) return; // Don't handle click if we're scrubbing
    const newTime = getTimeFromEvent(e);
    dispatch(seek(newTime));
  };

  // Handle seeking bar mouse/touch down (start scrubbing)
  const handleSeekingBarMouseDown = (e) => {
    e.preventDefault();
    const newTime = getTimeFromEvent(e);
    dispatch(startScrub(newTime));

    let latestScrubTime = newTime;

    const onMove = (ev) => {
      ev.preventDefault(); // Prevent scrolling on mobile
      const t = getTimeFromEvent(ev);
      latestScrubTime = t;
      dispatch(updateScrub(t));
    };

    const onUp = () => {
      dispatch(endScrub());
      // Commit to playback time using latest scrubTime
      dispatch(seek(latestScrubTime));
      setIsTouchActive(false); // Reset touch state
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };

    // Support both mouse and touch events
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
  };

  // Handle touch start (for mobile)
  const handleSeekingBarTouchStart = (e) => {
    e.preventDefault();
    setIsTouchActive(true); // Mark touch as active for larger playhead
    handleSeekingBarMouseDown(e);
  };

  // Check if buttons should be disabled
  // Use scrubTime if scrubbing, otherwise currentTime
  const displayTime = isScrubbing ? scrubTime : currentTime;

  const determineCurrentSectionIndex = () => {
    if (!timelineItems.length) return -1;

    if (currentItem) {
      const selectedIndex = timelineItems.findIndex((item) => item.id === currentItem.id);
      if (selectedIndex >= 0) {
        return selectedIndex;
      }
    }

    const epsilon = 0.05;
    let inferredIndex = -1;

    for (let index = 0; index < timelineItems.length; index += 1) {
      const item = timelineItems[index];
      if (displayTime + epsilon >= item.start_time) {
        inferredIndex = index;
      } else {
        break;
      }
    }

    if (inferredIndex >= 0) {
      return inferredIndex;
    }

    return 0;
  };

  const currentSectionIndex = determineCurrentSectionIndex();

  const previousSection = currentSectionIndex > 0 ? timelineItems[currentSectionIndex - 1] : null;
  const nextSection =
    currentSectionIndex >= 0 && currentSectionIndex < timelineItems.length - 1
      ? timelineItems[currentSectionIndex + 1]
      : null;

  const canRewind = Boolean(previousSection);
  const canFastForward = Boolean(nextSection);

  // Handle rewind (15 seconds backward)
  const handleRewind = () => {
    if (!previousSection) return;
    dispatch(seek(previousSection.start_time));
    // Start playback if paused (matching legacy behavior)
    if (!isPlaying) {
      dispatch(play());
    }
  };

  // Handle fast forward (15 seconds forward)
  const handleFastForward = () => {
    if (!nextSection) return;
    dispatch(seek(nextSection.start_time));

    if (!isPlaying) {
      // Start playback if paused (matching legacy behavior)
      dispatch(play());
    }
  };

  // Check if we should act like Continue button (same pattern as PreviewRenderers)
  // This happens when paused at the end of non-video/audio items
  const shouldActLikeContinue = () => {
    if (isPlaying || !currentItem || !currentAsset) return false;

    // Only for non-video/audio asset types (same as PreviewRenderers)
    const nonPlayableTypes = ['document', 'text', 'question', 'assigned-section'];
    if (!nonPlayableTypes.includes(currentAsset.type)) return false;

    // Check if we're paused at the end of this item (same logic as PreviewRenderers)
    const itemEnd = currentItem.start_time + currentItem.duration;
    const stopTime = itemEnd - 0.02;
    const isAtEnd = !isPlaying && Math.abs(currentTime - stopTime) < 0.01;

    return isAtEnd;
  };

  // Handle play/pause with Continue-like behavior
  const handlePlayPause = () => {
    if (shouldActLikeContinue()) {
      // Act like Continue button
      const currentIndex = timelineItems.findIndex((item) => item.id === currentItem.id);
      const nextItem = currentIndex >= 0 && currentIndex < timelineItems.length - 1
        ? timelineItems[currentIndex + 1]
        : null;

      if (nextItem) {
        // Seek to the next item's start time and play
        dispatch(seek(nextItem.start_time));
        dispatch(play());
      } else {
        // Last item - just pause and seek to end
        dispatch(pause());
        dispatch(seek(totalDuration));
      }
    } else {
      // Normal play/pause toggle
      dispatch(togglePlayPause());
    }
  };

  // Handle fullscreen toggle
  const toggleFullscreen = (event) => {
    event.stopPropagation();

    if (!fullscreenContainerRef?.current) {
      return;
    }

    if (document.fullscreenElement) {
      document.exitFullscreen().then(() => {
        dispatch(setIsFullscreen(false));
      }).catch((error) => {
        console.error('Error exiting fullscreen:', error);
      });
    } else if (fullscreenContainerRef.current.requestFullscreen) {
      fullscreenContainerRef.current.requestFullscreen().then(() => {
        dispatch(setIsFullscreen(true));
      }).catch((error) => {
        console.error('Error requesting fullscreen:', error);
      });
    }
  };

  // Check if fullscreen is supported
  const isFullscreenSupported = fullscreenContainerRef?.current?.requestFullscreen;

  // Toggle mute audio
  const toggleMuteAudio = (event) => {
    event.stopPropagation();
    
    // Find the video element
    const findVideoElement = () => {
      if (fullscreenContainerRef?.current) {
        const video = fullscreenContainerRef.current.querySelector('video');
        if (video) return video;
      }
      return document.querySelector('video');
    };

    const videoElement = findVideoElement();
    if (videoElement) {
      videoElement.muted = !videoElement.muted;
      setIsVideoMuted(videoElement.muted);
    }
  };


  return (
    <div
      style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        padding: '0px 16px 8px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        zIndex: 10,
      }}
    >
      {/* Seeking bar track */}
      <div
        ref={seekingBarRef}
        onClick={handleSeekingBarClick}
        onMouseDown={handleSeekingBarMouseDown}
        onTouchStart={handleSeekingBarTouchStart}
        style={{
          position: 'relative',
          width: '100%',
          // Taller on mobile for better touch target (44px minimum), visual stays 6px
          height: isMobile ? 44 : 6,
          // Use padding to create larger touch area while keeping visual track the same
          padding: isMobile ? '19px 0' : 0,
          background: 'transparent',
          borderRadius: 3,
          cursor: 'pointer',
          touchAction: 'none', // Prevent default touch behaviors like scrolling
        }}
      >
        {/* Visual track - actual visible bar */}
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            transform: 'translateY(-50%)',
            height: 6,
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: 3,
          }}
        >
          {/* Timeline blocks */}
          {timelineItems.map((item) => {
            const asset = allAssets.find((a) => a.id === item.asset_id);
            if (!asset) return null;

            const blockStart = item.start_time / totalDuration;
            const blockWidth = item.duration / totalDuration;
            // Distinct subtle colors per type for better readability
            // video: gray, image: blue-ish, document: teal-ish, question: amber-ish, text: purple-ish
            let blockColor = '#6b7280'; // video (default)
            if (asset.type === 'image') {
              blockColor = '#93c5fd';
            } else if (asset.type === 'document') {
              blockColor = '#99f6e4';
            } else if (asset.type === 'question') {
              blockColor = '#fde68a';
            } else if (asset.type === 'text') {
              blockColor = '#e9d5ff';
            }

            return (
              <div
                key={item.id}
                style={{
                  position: 'absolute',
                  left: `${blockStart * 100}%`,
                  width: `${blockWidth * 100}%`,
                  height: '100%',
                  background: blockColor,
                  borderRadius: 3,
                  opacity: 0.7,
                }}
              />
            );
          })}

          {/* Played progress */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              width: `${((isScrubbing ? scrubTime : currentTime) / effectiveDuration) * 100}%`,
              height: '100%',
              background: '#F8585C',
              borderRadius: 3,
              zIndex: 2,
            }}
          />
        </div>

        {/* Playhead - positioned relative to seeking bar track container */}
        <div
          style={{
            position: 'absolute',
            left: `${((isScrubbing ? scrubTime : currentTime) / effectiveDuration) * 100}%`,
            top: '50%',
            transform: 'translate(-50%, -50%)',
            // Larger touch target on mobile (44px minimum for accessibility), visual size stays the same
            width: isMobile ? 44 : 18,
            height: isMobile ? 44 : 18,
            // Use padding to create larger touch area while keeping visual size consistent
            padding: (() => {
              if (!isMobile) return 0;
              return (isScrubbing || isTouchActive) ? 8 : 13;
            })(),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 3,
            pointerEvents: 'none',
            touchAction: 'none', // Ensure touch events pass through to parent
            transition: 'padding 0.15s ease', // Smooth size transition
          }}
        >
          {/* Visual playhead circle - actual visible element */}
          <div
            style={{
              width: (isMobile && (isScrubbing || isTouchActive)) ? 28 : 18,
              height: (isMobile && (isScrubbing || isTouchActive)) ? 28 : 18,
              background: '#F8585C',
              borderRadius: '50%',
              border: '2px solid #fff',
              boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
              transition: 'width 0.15s ease, height 0.15s ease', // Smooth size transition
            }}
          />
        </div>
      </div>

      {/* Time display and controls (hidden while recording/countdown or armed) */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 50, // Increased from 41px to 50px to give more room
          position: 'relative',
        }}
      >
        {/* Recording controls centered */}
        {(isTimerRunning || isRecording || isCameraEnabled || isScreenEnabled || isAudioEnabled) && (
          <div
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
        <RecordingControls
          onStartRecording={onStartRecording}
          onPauseRecording={onPauseRecording}
          onResumeRecording={onResumeRecording}
          onStopRecording={onStopRecording}
          onCancelRecording={onCancelRecording}
        />
          </div>
        )}
        {!isTimerRunning && !isRecording && hasMultipleCameras && isCameraEnabled && !isScreenEnabled && !isAudioEnabled && (
          <button
            type="button"
            onClick={onSwitchCamera}
            style={{
              position: 'absolute',
              left: 16,
              height: 40,
              padding: '0 14px',
              borderRadius: 8,
              border: 0,
              background: '#6B7280',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            Switch Camera
          </button>
        )}
        {!(isTimerRunning || isRecording || isCameraEnabled || isScreenEnabled || isAudioEnabled) && (
          <>
            {/* Timecode aligned to the left */}
            <span style={{ fontSize: 13, color: '#ffffff', fontFamily: 'monospace' }}>
              {formatTime(isScrubbing ? scrubTime : currentTime)} / {formatTime(effectiveDuration)}
            </span>
            {/* Buttons centered */}
            <div
              style={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
            <button
              type="button"
                onClick={handleRewind}
                disabled={!canRewind}
              style={{
                width: 44,
                height: 44,
                  borderRadius: '50%',
                  background: '#F8585C',
                  border: 0,
                  color: 'white',
                  cursor: canRewind ? 'pointer' : 'default',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  opacity: canRewind ? 1 : 0.5,
                }}
              >
                <img
                  src={rewindIcon}
                  alt="Rewind 15 seconds"
                  style={{ width: 24, height: 24, filter: 'brightness(0) invert(1)' }}
                />
              </button>
              <button
                type="button"
                onClick={handlePlayPause}
                style={{
                  width: 56,
                  height: 56,
                borderRadius: '50%',
                background: '#F8585C',
                border: 0,
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              <img
                src={isPlaying ? videoPlayerPauseIcon : videoPlayerPlayIcon}
                alt={isPlaying ? 'Pause' : 'Play'}
                  style={{ width: 32, height: 32 }}
                />
              </button>
              <button
                type="button"
                onClick={handleFastForward}
                disabled={!canFastForward}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: '#F8585C',
                  border: 0,
                  color: 'white',
                  cursor: canFastForward ? 'pointer' : 'default',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  opacity: canFastForward ? 1 : 0.5,
                }}
              >
                <img
                  src={rewindIcon}
                  alt="Fast forward 15 seconds"
                  style={{ width: 24, height: 24, transform: 'rotate(180deg)', filter: 'brightness(0) invert(1)' }}
              />
            </button>
            </div>
            {/* Fullscreen and Volume buttons aligned to the right */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginLeft: 'auto' }}>
              {isFullscreenSupported && (
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    background: 'transparent',
                    border: 0,
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                  }}
                >
                  <img
                    src={fullscreenIcon}
                    alt="Toggle fullscreen"
                    style={{ width: 24, height: 24, filter: 'brightness(0) invert(1)' }}
                  />
                </button>
              )}
              {/* Volume/Mute button */}
              <button
                type="button"
                onClick={toggleMuteAudio}
                disabled={currentAsset?.type !== 'video'}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  background: 'transparent',
                  border: 0,
                  color: 'white',
                  cursor: currentAsset?.type === 'video' ? 'pointer' : 'default',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  opacity: currentAsset?.type === 'video' ? 1 : 0.5,
                }}
              >
                <img
                  src={isVideoMuted ? mutedVolumeIcon : volumeIcon}
                  alt={isVideoMuted ? 'Unmute' : 'Mute'}
                  style={{ width: 24, height: 24, filter: 'brightness(0) invert(1)' }}
                />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
